import * as React from 'react';
import { Box, Grid, styled, Typography } from '@mui/material';

import { IOpeningHours } from '@pepperhq/types-sdk';
import { DAY_NUMBER_TO_DAY, formatRange, getDaySlots } from './openingHoursHelpers';
import { format } from 'date-fns';

const StyledGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(0.5, 1),
    '&.active': {
        backgroundColor: theme.palette.divider,
        fontWeight: theme.typography.fontWeightBold,
        borderRadius: theme.shape.borderRadius
    }
}));

interface IProps {
    openingHours: IOpeningHours<string>;
}

export function LocationWorkingHours({ openingHours }: IProps) {
    const daySlots = React.useMemo(() => {
        const today = new Date();

        return Array.from({ length: 7 }, (_, i) => {
            const dayDate = new Date(today);
            dayDate.setDate(today.getDate() + i);
            const dayDateStr = format(dayDate, 'yyyy-MM-dd');

            // JS getDay() is 0=Sun..6=Sat; DAY_NUMBER_TO_DAY uses 0=Mon..6=Sun
            const day = DAY_NUMBER_TO_DAY[(dayDate.getDay() + 6) % 7];
            const isToday = i === 0;

            const slots = getDaySlots(openingHours.regular, day);
            const isOverrideClosed = openingHours.overrides
                ?.flatMap(override => override.closedDates ?? [])
                .some(date => date === dayDateStr);
            const hasOverrideHours =
                openingHours.overrides
                    ?.flatMap(override => override.dates ?? [])
                    .some(slot => slot.openTime.date === dayDateStr) ?? false;
            const activeSlots = hasOverrideHours
                ? openingHours.overrides
                      ?.flatMap(override => override.dates ?? [])
                      .filter(slot => slot.openTime.date === dayDateStr) ?? []
                : slots;

            return {
                day,
                formattedSlots: activeSlots.map(slot => formatRange(slot)),
                isClosed: !!isOverrideClosed || activeSlots.length === 0,
                isToday,
                hasOverrideHours
            };
        });
    }, [openingHours.regular, openingHours.overrides]);

    return (
        <div>
            {daySlots.map(day => (
                <StyledGrid className={day.isToday ? 'active' : ''} container key={day.day}>
                    <Grid size={4}>
                        <Box display="flex" flexDirection="column">
                            <Typography
                                sx={{ textTransform: 'capitalize', fontWeight: 'inherit' }}
                                variant="body1"
                            >
                                {day.day}
                            </Typography>
                            {day.hasOverrideHours && (
                                <Typography variant="caption" color="text.secondary">
                                    (Special hours)
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                    <Grid size={6}>
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="flex-end"
                            justifyContent="center"
                        >
                            {!day.isClosed &&
                                day.formattedSlots.map(slot => (
                                    <Typography key={slot} sx={{ fontWeight: 'inherit' }} variant="body2">
                                        {slot}
                                    </Typography>
                                ))}
                            {day.isClosed && (
                                <Typography sx={{ fontWeight: 'inherit' }} variant="body2">
                                    Closed
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                </StyledGrid>
            ))}
        </div>
    );
}
